import { FileItemModel } from '@models/module/fields/main';
import { ExplorerBreadcrumbItem, ExplorerFolderMeta } from '../models/file-explorer.models';

type ExplorerIdentitySource = Pick<FileItemModel, 'id' | 'isFolder'> &
  Partial<Pick<FileItemModel, 'itemModuleId' | 'parentEntityInstanceId' | 'parentsEIString' | 'name'>> & {
    isEntityInstance?: boolean;
    explorerKey?: string;
    parentExplorerKey?: string;
  };

export function isExplorerEntityInstance(item: Partial<ExplorerIdentitySource>): boolean {
  return !!item?.isEntityInstance || item?.itemModuleId != null;
}

/**
 * Identity of one rendered occurrence, not merely of the underlying database object. Global Project
 * Files items keep their database id across contracts, so the entity lineage is part of the key.
 * Folder/file ids are already unique inside that lineage; including the immediate parent would make
 * the same occurrence acquire a different identity while navigating. This is the typed equivalent
 * of legacy Explorer1.computeFolderId.
 */
export function getExplorerItemKey(item: ExplorerIdentitySource, fallbackEntityInstanceId?: number): string {
  if (item.explorerKey) {
    return item.explorerKey;
  }
  if (isExplorerEntityInstance(item)) {
    return `entity:${item.id}`;
  }
  if (item.id < 0) {
    return `system:${item.id}`;
  }

  const kind = item.isFolder ? 'folder' : 'file';
  const entityLineage = item.parentsEIString || `${item.parentEntityInstanceId ?? fallbackEntityInstanceId ?? ''}`;
  return `${kind}:${entityLineage}:${item.id}`;
}

export function getExplorerItemEntityInstanceId(
  item: Partial<ExplorerIdentitySource> | null | undefined,
  fallbackEntityInstanceId?: number,
): number {
  return isExplorerEntityInstance(item) ? item.id : (item?.parentEntityInstanceId ?? fallbackEntityInstanceId);
}

export function toExplorerFolderMeta(
  item: ExplorerIdentitySource,
  fallbackEntityInstanceId?: number,
  parentExplorerKey?: string,
): ExplorerFolderMeta {
  const fileItem = item as FileItemModel;
  return {
    explorerKey: getExplorerItemKey(item, fallbackEntityInstanceId),
    parentExplorerKey: item.parentExplorerKey ?? parentExplorerKey,
    parentEntityInstanceId: getExplorerItemEntityInstanceId(item, fallbackEntityInstanceId),
    parentsEIString: item.parentsEIString,
    isEntityInstance: isExplorerEntityInstance(item),
    itemModuleId: item.itemModuleId,
    isLocked: fileItem.isLocked,
    permissions: fileItem.permissions,
    destinationEntityInstanceId: fileItem.destinationEntityInstanceId,
  };
}

export function breadcrumbItemToMeta(item: ExplorerBreadcrumbItem): ExplorerFolderMeta {
  return {
    explorerKey: item.explorerKey,
    parentExplorerKey: item.parentExplorerKey,
    parentEntityInstanceId: item.parentEntityInstanceId,
    parentsEIString: item.parentsEIString,
    isEntityInstance: item.isEntityInstance,
    itemModuleId: item.itemModuleId,
    destinationEntityInstanceId: item.destinationEntityInstanceId,
  };
}
